import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import type {
  ResolvedBottomTab,
  ResolvedNavGroup,
} from '../../shared/nav/canonical-nav.js';
import { NavIcon } from '../../shared/nav/icons.js';

/**
 * Мобильный нижний таб-бар (<768px) по дизайну manager v3 §2.2.
 *
 * Sticky bottom-tabs: до 4 первичных табов (по роли) + 5-й таб «Меню»
 * (hamburger), раскрывающий drawer со ВСЕМИ остальными пунктами навигации,
 * карточкой пользователя и кнопкой «Выйти» (logout живёт в drawer на телефоне,
 * т.к. сайдбар скрыт). Touch target ≥44px (Apple HIG).
 *
 * На desktop/tablet (≥768) скрыт (CSS `display:none`).
 */
export interface BottomTabsProps {
  tabs: readonly ResolvedBottomTab[];
  /** Все группы навигации — для drawer «Меню/ещё». */
  groups: readonly ResolvedNavGroup[];
  /** Бренд «LovArt · <Отель>» для шапки drawer. */
  brand: string;
  /** Лейбл таба «Меню» (i18n). */
  menuLabel: string;
  /** Заголовок drawer (i18n, напр. «Навигация»). */
  drawerTitle: string;
  actorName: string;
  /** Подпись пользователя (email/роль). */
  actorMeta: string;
  userInitials: string;
  logoutLabel: string;
  onLogout: () => void;
}

export function BottomTabs({
  tabs,
  groups,
  brand,
  menuLabel,
  drawerTitle,
  actorName,
  actorMeta,
  userInitials,
  logoutLabel,
  onLogout,
}: BottomTabsProps): JSX.Element {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();

  const go = (path: string): void => {
    setDrawerOpen(false);
    navigate(path);
  };

  return (
    <>
      {drawerOpen && (
        <div
          className="ta-drawer"
          role="dialog"
          aria-modal="true"
          aria-label={drawerTitle}
          data-testid="nav-drawer"
        >
          <div
            className="ta-drawer__backdrop"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div className="ta-drawer__panel">
            <div className="ta-drawer__head">
              <span className="ta-drawer__brand">{brand}</span>
              <button
                type="button"
                className="ta-drawer__close"
                onClick={() => setDrawerOpen(false)}
                aria-label="Закрыть меню"
              >
                ✕
              </button>
            </div>

            {/* Карточка пользователя — телефон (§2.2). */}
            <div className="ta-drawer__user">
              <span className="ta-avatar" aria-hidden="true">
                {userInitials}
              </span>
              <div className="ta-drawer__user-meta">
                <div className="ta-drawer__user-name">{actorName}</div>
                <div className="ta-drawer__user-sub">{actorMeta}</div>
              </div>
            </div>

            <nav className="ta-drawer__nav">
              {groups.map((group) => (
                <div key={group.key} className="ta-drawer__group">
                  <div className="ta-drawer__group-title">{group.label}</div>
                  {group.items.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      className="ta-drawer__link"
                      data-testid={`drawer-nav-${item.key}`}
                      onClick={() => go(item.path)}
                    >
                      <NavIcon name={item.icon} size={20} className="ta-drawer__icon" />
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              ))}
            </nav>

            {/* «Выйти» — в drawer на телефоне (сайдбар скрыт). */}
            <button
              type="button"
              className="ta-drawer__logout"
              onClick={onLogout}
              data-testid="drawer-logout"
            >
              <NavIcon name="sign-out" size={18} className="ta-drawer__icon" />
              <span>{logoutLabel}</span>
            </button>
          </div>
        </div>
      )}

      <nav className="ta-bottom-tabs" aria-label="Нижняя навигация" data-testid="bottom-tabs">
        {tabs.map((tab) => (
          <NavLink
            key={tab.key}
            to={tab.path}
            end={tab.path === '/'}
            className={({ isActive }) => `ta-bottom-tabs__tab${isActive ? ' active' : ''}`}
            data-testid={`tab-${tab.key}`}
          >
            <NavIcon name={tab.icon} size={22} className="ta-bottom-tabs__icon" />
            <span className="ta-bottom-tabs__label">{tab.label}</span>
          </NavLink>
        ))}
        <button
          type="button"
          className={`ta-bottom-tabs__tab${drawerOpen ? ' active' : ''}`}
          onClick={() => setDrawerOpen((v) => !v)}
          data-testid="tab-more"
          aria-expanded={drawerOpen}
        >
          <NavIcon name="list" size={22} className="ta-bottom-tabs__icon" />
          <span className="ta-bottom-tabs__label">{menuLabel}</span>
        </button>
      </nav>
    </>
  );
}
